import { useEffect, useMemo, useState } from 'react';
import SCHOOL_LIST from 'constants/schools';
import getSchoolList from 'features/schools/api/getSchoolList';

export type SchoolOption = {
  label: string;
  value: string;
};

/**
 * Loads school names from the API, falling back to the bundled static list
 * until the request succeeds (or if it fails).
 */
export default function useSchoolList() {
  const [schools, setSchools] = useState<string[]>(SCHOOL_LIST);
  const [loading, setLoading] = useState(true);
  const [fromApi, setFromApi] = useState(false);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        const list = await getSchoolList();
        if (!mounted || list.length === 0) return;
        setSchools(list);
        setFromApi(true);
      } catch {
        // Keep static fallback — signup/profile remain usable offline.
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();

    return () => {
      mounted = false;
    };
  }, []);

  const schoolOptions: SchoolOption[] = useMemo(
    () => schools.map(name => ({ label: name, value: name })),
    [schools],
  );

  const knownSchoolValues = useMemo(
    () => new Set(schools.map(name => name.toLocaleLowerCase())),
    [schools],
  );

  return {
    schools,
    schoolOptions,
    knownSchoolValues,
    loading,
    fromApi,
  };
}
